import { trackerEventNames } from './Constants';
import Tracker from './Tracker';

declare let window: any;

export type GtmSource = 'google' | 'flow';

export type GtmState = 'none' | 'registered' | 'ok';

export interface GtmLoadReport {
  gtmSrc: GtmSource;
  gtm: GtmState;
  posthog: boolean;
  fallback: boolean;
}

export const GTM_LOAD_CHECK_MS = 15000;

/**
 * CJT-15943: 15 s after the DE container was injected, report the page loads where GTM or PostHog didn't come up, or
 * where the proxy failed and Google's URL was loaded instead. Healthy page loads send nothing; the main measure stays
 * the share of leads without any PostHog event. New Relic Log, GTM_LOAD_REPORTED, fields under data.* (the event
 * tracker drops other fields).
 *
 * gtm: 'none' = no container object (blocked, or a blocker's stub), 'registered' = gtm.js created the object (with
 * bootstrap 0) but the container never finished, 'ok' = bootstrap set. posthog: posthog-js replaced the snippet stub.
 */
class GtmLoadMonitor {
  private readonly tracker: Tracker;

  private readonly gtmId: string;

  private fallback = false;

  constructor(tracker: Tracker, gtmId: string) {
    this.tracker = tracker;
    this.gtmId = gtmId;
  }

  public start(gtmSrc: GtmSource): void {
    setTimeout(() => this.report(gtmSrc), GTM_LOAD_CHECK_MS);
  }

  /** The proxy failed and Google's URL was injected instead. */
  public markFallback(): void {
    this.fallback = true;
  }

  private report(gtmSrc: GtmSource): void {
    const container = window.google_tag_manager?.[this.gtmId];
    let gtm: GtmState = 'none';

    if (container) {
      gtm = typeof container.bootstrap === 'number' && container.bootstrap > 0 ? 'ok' : 'registered';
    }

    const posthog = window.posthog?.__loaded === true;

    if (gtm === 'ok' && posthog && !this.fallback) {
      return;
    }

    const report: GtmLoadReport = { gtmSrc, gtm, posthog, fallback: this.fallback };

    this.tracker.track(trackerEventNames.GTM_LOAD_REPORTED, { data: report });
  }
}

export default GtmLoadMonitor;
